import { useEffect } from 'react';

interface UseNavigationBlockerProps {
    isBlocked: boolean;
    onBlockedNavigation?: () => void;
    message?: string;
}

export const useNavigationBlocker = ({
    isBlocked,
    onBlockedNavigation,
    message = 'You have unsaved changes. Are you sure you want to leave?'
}: UseNavigationBlockerProps) => {
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isBlocked) {
                event.preventDefault();
                event.returnValue = message;
                return message;
            }
        };

        const handlePopState = (event: PopStateEvent) => {
            if (isBlocked) {
                event.preventDefault();
                if (onBlockedNavigation) {
                    onBlockedNavigation();
                }
                // Push the current state back to prevent navigation
                window.history.pushState(null, '', window.location.href);
            }
        };

        // Handle React Router link clicks and other navigation attempts
        const handleClick = (event: MouseEvent) => {
            if (!isBlocked) return;

            const target = event.target as HTMLElement;
            const link = target.closest('a, [role="button"], button');
            
            if (!link) return;

            // Check if it's a React Router link or navigation element
            const isRouterLink = link.hasAttribute('href') || 
                                link.getAttribute('role') === 'button' ||
                                link.tagName === 'BUTTON' ||
                                link.classList.contains('nav-link') ||
                                link.closest('[data-testid*="link"]') ||
                                link.closest('[data-testid*="nav"]');

            // Skip if it's not a navigation element or if it's a download link
            if (!isRouterLink || 
                link.getAttribute('download') ||
                link.getAttribute('target') === '_blank' ||
                link.getAttribute('href')?.startsWith('mailto:') ||
                link.getAttribute('href')?.startsWith('tel:')) {
                return;
            }

            // Check if the link would navigate to a different route
            const href = link.getAttribute('href');
            const currentPath = window.location.pathname;
            
            if (href && href !== currentPath && !href.startsWith('#')) {
                // Check if it's a child route navigation (should be allowed)
                const isChildRoute = href.startsWith(currentPath + '/') || 
                                   (currentPath.endsWith('/') && href.startsWith(currentPath));
                
                // Allow child route navigation
                if (isChildRoute) {
                    return; // Allow the navigation to proceed
                }
                
                event.preventDefault();
                event.stopPropagation();
                
                if (onBlockedNavigation) {
                    onBlockedNavigation();
                }
                return false;
            }
        };

        // Add event listeners
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);
        document.addEventListener('click', handleClick, true); // Use capture phase

        // Push current state to enable popstate detection
        window.history.pushState(null, '', window.location.href);

        // Cleanup function
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
            document.removeEventListener('click', handleClick, true);
        };
    }, [isBlocked, onBlockedNavigation, message]);
}; 